import React from "react";
import { Box, Button, FormControl, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { AddressAutocomplete } from "components/common/AddressAutocomplete";
import { InputBase, InputLabel } from "components/common/Input";
import { Location } from "types/location";

export type LocationFormValues = {
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  description: string;
};

export function emptyLocationForm(): LocationFormValues {
  return {
    name: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    description: "",
  };
}

export function locationToForm(loc: Location): LocationFormValues {
  return {
    name: loc.name || "",
    address: loc.address || "",
    address2: loc.address2 || "",
    city: loc.city || "",
    state: loc.state || "",
    zipcode: loc.zipcode || "",
    country: loc.country || "",
    description: loc.description || "",
  };
}

type LocationFormProps = {
  values: LocationFormValues;
  /** Use functional updates so rapid merges (e.g. Places `onAddressSelect` + `onChange`) do not clobber fields. */
  onChange: React.Dispatch<React.SetStateAction<LocationFormValues>>;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
  cancelTo: string;
};

export default function LocationForm({
  values,
  onChange,
  onSubmit,
  submitLabel,
  cancelTo,
}: LocationFormProps) {
  const patch = (partial: Partial<LocationFormValues>) => {
    onChange((prev) => ({ ...prev, ...partial }));
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Box sx={{ mt: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Location details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search for an address to auto-fill the fields, or enter them manually.
        </Typography>

        <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="location-name">
            Nickname
          </InputLabel>
          <InputBase
            id="location-name"
            placeholder="HQ"
            value={values.name}
            onChange={(e) => patch({ name: e.target.value })}
            inputProps={{ required: true }}
          />
        </FormControl>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mb: 2,
          }}
        >
          <AddressAutocomplete
            label="Address"
            value={values.address}
            onChange={(v) => patch({ address: v })}
            onAddressSelect={(fields) => {
              if (typeof fields === "string") return;
              patch({
                address: fields.address,
                address2: fields.address2,
                city: fields.city,
                state: fields.state,
                zipcode: fields.zipcode,
                country: fields.country,
              });
            }}
          />

          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="location-address2">
              Address line 2
            </InputLabel>
            <InputBase
              id="location-address2"
              placeholder="Suite, unit, building"
              value={values.address2}
              onChange={(e) => patch({ address2: e.target.value })}
            />
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 3,
            mb: 2,
          }}
        >
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="location-city">
              City
            </InputLabel>
            <InputBase
              id="location-city"
              placeholder="City"
              value={values.city}
              onChange={(e) => patch({ city: e.target.value })}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="location-state">
              State / province
            </InputLabel>
            <InputBase
              id="location-state"
              placeholder="State"
              value={values.state}
              onChange={(e) => patch({ state: e.target.value })}
            />
          </FormControl>

          <FormControl variant="standard" fullWidth>
            <InputLabel shrink htmlFor="location-zipcode">
              Postal code
            </InputLabel>
            <InputBase
              id="location-zipcode"
              placeholder="Postal code"
              value={values.zipcode}
              onChange={(e) => patch({ zipcode: e.target.value })}
            />
          </FormControl>
        </Box>

        <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="location-country">
            Country
          </InputLabel>
          <InputBase
            id="location-country"
            placeholder="Country"
            value={values.country}
            onChange={(e) => patch({ country: e.target.value })}
          />
        </FormControl>

        <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink htmlFor="location-description">
            Building details
          </InputLabel>
          <InputBase
            id="location-description"
            placeholder="Loading dock, parking, elevator, front desk / insurance requirements, etc."
            multiline
            minRows={3}
            value={values.description}
            onChange={(e) => patch({ description: e.target.value })}
          />
        </FormControl>
      </Box>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained">
          {submitLabel}
        </Button>
        <Button component={RouterLink} to={cancelTo} variant="outlined">
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
